// src/composables/useInstallPrompt.js
// Handles both Android (beforeinstallprompt) and iOS (navigator.standalone) install flows.
//
// iOS detection notes:
//   navigator.standalone === true  → already installed as PWA (hide all install UI)
//   navigator.standalone === false → iOS Safari browser (show guide)
//   navigator.standalone === undefined → not iOS (show Android flow)
//
// Android notes:
//   beforeinstallprompt only fires when: valid manifest + registered SW + HTTPS/localhost
//   It does NOT fire in vue-cli-service dev server (SW is not registered in development)
//   deferredPrompt.prompt() can only be called once — set to null after use

import { ref, onMounted, onBeforeUnmount } from 'vue';

export function useInstallPrompt() {
  const deferredPrompt = ref(null);
  const canInstall = ref(false);
  const isInstalled = ref(false);

  // iOS detection: navigator.standalone is a WebKit-only property
  // window.MSStream guard prevents false positives on IE11/Edge Legacy
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  // isStandalone: true when running as installed PWA (both Android and iOS)
  const isStandalone =
    navigator.standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches;

  function onBeforeInstallPrompt(e) {
    // Prevent the mini-infobar from appearing — we show our own UI
    e.preventDefault();
    deferredPrompt.value = e;
    canInstall.value = true;
  }

  function onAppInstalled() {
    deferredPrompt.value = null;
    canInstall.value = false;
    isInstalled.value = true;
  }

  onMounted(() => {
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.removeEventListener('appinstalled', onAppInstalled);
  });

  async function triggerInstall() {
    if (!deferredPrompt.value) return null;
    deferredPrompt.value.prompt();
    const { outcome } = await deferredPrompt.value.userChoice;
    // Event can only be used once — clear it regardless of outcome
    deferredPrompt.value = null;
    canInstall.value = false;
    return outcome; // 'accepted' | 'dismissed'
  }

  return {
    canInstall,
    isInstalled,
    isIOS,
    isStandalone,
    triggerInstall,
  };
}
